import '@fortawesome/fontawesome-free/css/all.min.css';
import { useContext, useEffect, useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CartContext from '../../contexts/CartContext';
import UserContext from '../../contexts/UserContext';
import { addToWishlist } from '../../services/userServices';
import apiClient, {
  IMAGE_BASE_URL,
  PRODUCT_CATALOG_ID,
} from '../../utils/api-client';
import useFacebookPixel from '../../utils/useFacebookPixel';
import CardSkeleton from '../Common/CardSkeleton';
import Loading from '../Common/Loading';
import NoProductFound from '../Common/NoProductFound';
import ReviewStars from '../Common/ReviewStars';
import ProductCard from './ProductCard';
import QuantityInput from './QuantityInput';
import './SingleProductPage.css';
const SingleProductPage = () => {
  const { addToCart } = useContext(CartContext);
  const { product_slug } = useParams();
  const [product, setProduct] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [productReview, setProductReview] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(0);
  const { user } = useContext(UserContext);
  const facebookPixel = useFacebookPixel();

  useEffect(() => {
    apiClient
      .get(`/product/${product_slug}`)
      .then((res) => {
        if (res.data.status === true) {
          setSelectedVariant(0); // Set default variant
          setSelectedImage(0);
          setQuantity(1);
          setIsLoading(false);
          if (facebookPixel) {
            facebookPixel.trackEvent('ViewContent', {
              content_ids: [res.data.data.product_id], // Product ID
              content_type: 'product', // Type
            });
          }
          setProduct(res.data.data);
        }
      })
      .catch((err) => setError(err.message));
  }, [product_slug]);
  useEffect(() => {
    if (product.varients && product.varients.length > 0) {
      setSelectedVariant(0);
    }
  }, [product]);
  const handleVariantClick = (index) => {
    setSelectedVariant(index);
    setSelectedImage(0);
    setQuantity(1);
  };
  const generateShortDescription = (description, maxLength) => {
    if (!description || !description.length) {
      return '';
    }
    if (description.length <= maxLength) {
      return description;
    }
    return description.substring(0, maxLength) + '...';
  };
  const shortDescription = generateShortDescription(
    product?.product_details,
    250
  );
  const product_ids = product.product_id;
  const user_id_fk = parseInt(user);
  const handleAddToWishlist = async () => {
    try {
      await addToWishlist({ user_id_fk, product_ids });
      toast.success('Product added to wishlist successfully!');
    } catch (error) {
      toast.error('Error adding product to wishlist:', error.message);
    }
  };
  useEffect(() => {
    apiClient
      .post('/related-products', {
        product_id: product.product_id,
        category_id_fk: product.category_id_fk,
      })
      .then((res) => {
        setRelatedProducts(res.data.data);
      })
      .catch((err) => {
        setError(err.message);
      });
  }, [product]);
  const handleAddToCart = (product, quantity, selectedVariant) => {
    // Track AddToCart Event
    if (facebookPixel) {
      facebookPixel.trackEvent('AddToCart', {
        content_ids: [product.product_id],
        content_type: 'product',
        content_name: product.product_name,
        currency: 'PKR',
        quantity: quantity,
        product_catalog_id: PRODUCT_CATALOG_ID, // Replace with your actual catalog ID
      });
    }
    addToCart(product, quantity, selectedVariant);
  };
  const productSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.product_name,
    image: `${IMAGE_BASE_URL}/${product.product_image}`,
    description: product.product_details,
    sku: product.product_id,
    mpn: product.product_id,
    brand: {
      '@type': 'Brand',
      name: 'Bakumia',
    },
    offers: {
      '@type': 'Offer',
      url: window.location.href,
      priceCurrency: 'PKR',
      price:
        product.varients && product.varients[selectedVariant]
          ? product.varients[selectedVariant].final_price
          : '',
      itemCondition: 'https://schema.org/NewCondition',
      availability:
        product.varients &&
        product.varients[selectedVariant] &&
        product.varients[selectedVariant].in_stock > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
    },
    id: product.product_id,
    price:
      product.varients && product.varients[selectedVariant]
        ? product.varients[selectedVariant].final_price
        : '',
    image_link: product.product_image,
    availability:
      product.varients &&
      product.varients[selectedVariant] &&
      product.varients[selectedVariant].in_stock > 0
        ? 'in stock'
        : 'out of stock',
    description: product.product_details,
    title: product.product_name,
    link: window.location.href,
  };
  if (isLoading) {
    return <Loading />;
  }

  const getProductReview = () => {
    apiClient
      .post(`/get-all-product-reviews`, {
        p_id_fk: product.product_id,
      })
      .then((res) => {
        if (res.data.status === true) {
          setProductReview(res.data.data);
        }
      })
      .catch((err) => setError(err.message));
  };

  return (
    <>
      <Helmet>
        <title>{product.meta_title}</title>
        <meta name="description" content={product.meta_desc} />
        <meta name="keywords" content={product.keywords} />
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Helmet>
      {/* SingleProductpage */}
      <section className="py-5">
        <div className="container-fluid">
          <div className="row g-4">
            {/* Product image column */}
            <aside className="col-lg-4 col-md-4">
              <div className="product-image-section">
                {/* Main Product Image */}
                <div className="main-image-container mb-3">
                  <div className="border rounded-4 overflow-hidden bg-light d-flex align-items-center justify-content-center position-relative">
                    <img
                      src={
                        product.varients &&
                        product.varients[selectedVariant] &&
                        product.varients[selectedVariant].varient_images &&
                        product.varients[selectedVariant].varient_images[
                          selectedImage
                        ]
                          ? `${IMAGE_BASE_URL}/${product.varients[selectedVariant].varient_images[selectedImage].image_path}`
                          : ''
                      }
                      alt={product.alt_content}
                      className="main-product-image"
                    />
                    {/* Image Navigation Arrows */}
                    {product.varients &&
                      product.varients[selectedVariant] &&
                      product.varients[selectedVariant].varient_images &&
                      product.varients[selectedVariant].varient_images.length >
                        1 && (
                        <>
                          <button
                            className="btn btn-light btn-sm position-absolute start-0 top-50 translate-middle-y ms-2 rounded-pill shadow-sm"
                            onClick={() =>
                              setSelectedImage((prev) =>
                                prev === 0
                                  ? product.varients[selectedVariant]
                                      .varient_images.length - 1
                                  : prev - 1
                              )
                            }
                            style={{ zIndex: 10 }}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <button
                            className="btn btn-light btn-sm position-absolute end-0 top-50 translate-middle-y me-2 rounded-pill shadow-sm"
                            onClick={() =>
                              setSelectedImage((prev) =>
                                prev ===
                                product.varients[selectedVariant].varient_images
                                  .length -
                                  1
                                  ? 0
                                  : prev + 1
                              )
                            }
                            style={{ zIndex: 10 }}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </>
                      )}
                  </div>
                </div>

                {/* Thumbnail Images */}
                <div className="thumbnail-container sticky-top">
                  <div className="d-flex gap-2">
                    {product.varients &&
                      product.varients[selectedVariant] &&
                      product.varients[selectedVariant].varient_images.map(
                        (image, index) => (
                          <div
                            key={index}
                            className={`thumbnail-wrapper ${
                              selectedImage === index ? 'active' : ''
                            }`}
                            onClick={() => setSelectedImage(index)}
                          >
                            <img
                              src={`${IMAGE_BASE_URL}/${image.image_path}`}
                              alt={image.rimg_alt_content}
                              className="thumbnail-image"
                            />
                          </div>
                        )
                      )}
                  </div>
                </div>

                <hr />
                <div className="d-md-block d-lg-none d-none pt-3">
                  <h5 className="product-title text-dark mb-2 fw-bold">
                    {product.product_name}
                    {product.varients[selectedVariant] && (
                      <span
                        className="variant-name d-block mt-1"
                        style={{ color: '#791a89' }}
                      >
                        {product.varients[selectedVariant].p_v_name}
                      </span>
                    )}
                  </h5>

                  <div className="rating-section d-flex align-items-center gap-2 mb-3">
                    <div className="star-rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`fas fa-star ${
                            star <= (product.avg_rating || 0)
                              ? 'text-warning'
                              : 'text-muted'
                          }`}
                          style={{ fontSize: '0.9rem' }}
                        ></i>
                      ))}
                    </div>
                    <span className="rating-text small text-muted">
                      ({product.count_rating || 0} reviews)
                    </span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Product details column */}
            <main className="col-lg-8 col-md-8 product_details_coloumn">
              {error && <em className="form_error">{error}</em>}
              {product.varients && product.varients.length > 0 && (
                <div
                  style={{ maxHeight: '65vh' }}
                  className="product-details mt-0 overflow-auto d-flex flex-column"
                >
                  {/* Product Title and Rating */}
                  <div className=" d-md-none d-lg-block">
                    <h5 className="product-title text-dark fw-bold">
                      <span
                        style={{
                          fontSize: 'clamp(0.8rem, 0.9rem + 0.4vw, 1.4rem)',
                        }}
                      >
                        {product.product_name}
                      </span>
                      {product.varients[selectedVariant] && (
                        <span
                          className="variant-name d-block"
                          style={{ color: '#791a89' }}
                        >
                          {product.varients[selectedVariant].p_v_name}
                        </span>
                      )}
                    </h5>

                    <div className="rating-section d-flex align-items-center gap-2 mb-3">
                      <div className="star-rating">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i
                            key={star}
                            className={`fas fa-star ${
                              star <= (product.avg_rating || 0)
                                ? 'text-warning'
                                : 'text-muted'
                            }`}
                            style={{ fontSize: '0.9rem' }}
                          ></i>
                        ))}
                      </div>
                      <span className="rating-text small text-muted">
                        ({product.count_rating || 0} reviews)
                      </span>
                    </div>
                  </div>

                  {/* Price Section */}
                  <div className="price-section mb-4">
                    <div className="d-flex align-items-baseline gap-2 mb-1">
                      <span
                        className="current-price h4 fw-bold"
                        style={{ color: '#791a89' }}
                      >
                        Rs{' '}
                        {product.varients[selectedVariant] &&
                          product.varients[selectedVariant].final_price}
                      </span>
                      {product.varients[selectedVariant] &&
                        product.varients[selectedVariant].final_price <
                          product.varients[selectedVariant].product_price && (
                          <span className="original-price h6 text-muted text-decoration-line-through">
                            Rs {product.varients[selectedVariant].product_price}
                          </span>
                        )}
                    </div>
                    <small className="text-muted">Per item</small>
                  </div>

                  {/* Product Description */}
                  <div
                    style={{
                      fontSize: 'clamp(0.7rem, 0.7rem + 0.25vw, 1rem)',
                    }}
                    className="description-section mb-4"
                  >
                    <div
                      style={{ fontWeight: 400 }}
                      className="text-dark"
                      dangerouslySetInnerHTML={{
                        __html: shortDescription,
                      }}
                    />
                  </div>

                  {/* Product Information */}
                  <div className="product-info mb-4">
                    <div className="row g-2 mb-3">
                      <div className="col-4 col-sm-3">
                        <small className="text-muted fw-medium">
                          Variant Color:
                        </small>
                      </div>
                      <div className="col-8 col-sm-9">
                        <small className="fw-bold" style={{ color: '#791a89' }}>
                          {product.varients[selectedVariant] &&
                            product.varients[selectedVariant].p_v_name}
                        </small>
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-4 col-sm-3">
                        <small className="text-muted fw-medium">
                          Availability:
                        </small>
                      </div>
                      <div className="col-8 col-sm-9">
                        <small
                          className={`fw-medium ${
                            product.varients &&
                            product.varients[selectedVariant] &&
                            product.varients[selectedVariant].in_stock > 0
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          <i
                            className={`${
                              product.varients &&
                              product.varients[selectedVariant] &&
                              product.varients[selectedVariant].in_stock > 0
                                ? 'fas fa-check-circle'
                                : 'fas fa-times-circle'
                            } me-1`}
                          ></i>
                          {product.varients &&
                          product.varients[selectedVariant] &&
                          product.varients[selectedVariant].in_stock > 0
                            ? 'In Stock'
                            : 'Out of Stock'}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Variant Selection */}
                  <div className="variant-selection mb-4">
                    <h6 className="fw-bold mb-3">Choose Variants:</h6>
                    <div className="variants-grid">
                      {product.varients &&
                        product.varients.map((variant, index) => (
                          <div
                            key={index}
                            className={`variant-option ${
                              selectedVariant === index ? 'selected' : ''
                            }`}
                            onClick={() => handleVariantClick(index)}
                          >
                            <div className="variant-image">
                              <img
                                src={
                                  variant.varient_images &&
                                  variant.varient_images[0]
                                    ? `${IMAGE_BASE_URL}/${variant.varient_images[0].image_path}`
                                    : ''
                                }
                                alt={variant.rimg_alt_content}
                              />
                            </div>
                            <small className="variant-label text-center mt-1 fw-medium">
                              {variant.p_v_name}
                            </small>
                          </div>
                        ))}
                    </div>
                  </div>

                  <hr />

                  {/* Quantity and Cart Section */}
                  <div className="quantity-cart-section">
                    <div className="quantity-section mb-4">
                      <h6 className="fw-bold mb-2">Quantity:</h6>
                      <QuantityInput
                        quantity={quantity}
                        setQuantity={setQuantity}
                        cartPage={false}
                        productId={product.product_id}
                        variantId={product.varients[selectedVariant].p_v_id}
                        variantQty={
                          product?.varients[selectedVariant]?.available_quantity
                        }
                      />
                    </div>

                    <div className="action-buttons d-flex gap-2">
                      <button
                        className="btn btn-primary flex-fill add_to_cart_btn"
                        onClick={() =>
                          handleAddToCart(
                            product,
                            quantity,
                            product.varients[selectedVariant]
                          )
                        }
                        disabled={
                          product &&
                          product.varients &&
                          product.varients[selectedVariant] &&
                          product.varients[selectedVariant].in_stock < 1
                        }
                        style={{
                          backgroundColor: '#791a89',
                          borderColor: '#791a89',
                        }}
                      >
                        <i
                          className={`me-1 ${
                            product &&
                            product.varients &&
                            product.varients[selectedVariant] &&
                            product.varients[selectedVariant].in_stock < 1
                              ? 'fa fa-times'
                              : 'fa fa-shopping-basket'
                          }`}
                        ></i>
                        {product &&
                        product.varients &&
                        product.varients[selectedVariant] &&
                        product.varients[selectedVariant].in_stock < 1
                          ? 'Out of Stock'
                          : 'Add to Cart'}
                      </button>
                      <Link
                        className="btn btn-outline-secondary wishlist-btn"
                        onClick={handleAddToWishlist}
                      >
                        <i className=" fa fa-heart fa-lg text-center"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* <!-- Product description & related section --> */}
      <section className="product-section container-fluid bg-white border-top">
        <div>
          <div className="product-tabs my-4">
            <Tabs
              defaultActiveKey="description"
              id="product-tab"
              fill
              variant="pills"
              className="custom-tabs"
              style={{
                fontSize: 'clamp(0.8rem, 0.7rem + 0.25vw, 1.1rem)',
              }}
            >
              <Tab eventKey="description" title="Description">
                <div
                  className="pt-4 px-3 text-secondary rounded shadow-sm"
                  style={{
                    fontSize: 'clamp(0.8rem, 0.7rem + 0.25vw, 1.1rem)',
                    lineHeight: 1.7,
                    letterSpacing: '0.2px',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-line',
                  }}
                  dangerouslySetInnerHTML={{ __html: product.product_details }}
                />
              </Tab>

              <Tab
                eventKey="Reviews"
                title="Reviews"
                onEnter={getProductReview}
              >
                <div className="pt-3">
                  {productReview && productReview.length > 0 ? (
                    productReview.map((review) => (
                      <div
                        className="d-flex align-items-start gap-3 p-2 p-md-3 border rounded mb-3"
                        key={review.id}
                      >
                        <img
                          src={`${IMAGE_BASE_URL}/${review.user?.profile_pic}`}
                          className="rounded-circle"
                          style={{
                            width: '43px',
                            height: '43px',
                            objectFit: 'cover',
                            flexShrink: 0, // prevent shrinking on small screens
                          }}
                          alt="User profile"
                        />
                        <div className="d-flex flex-column flex-grow-1">
                          <div
                            style={{
                              fontSize:
                                'clamp(0.9rem, 0.8rem + 0.29vw, 1.2rem)',
                            }}
                            className="fw-semibold d-flex justify-content-sm-start gap-sm-2 justify-content-between align-items-center mb-1"
                          >
                            <span
                              className="text-truncate"
                              style={{ maxWidth: '75%' }}
                            >
                              {review.user?.full_name}
                            </span>
                            <ReviewStars stars={review?.rating} />
                          </div>
                          <p
                            style={{
                              fontSize:
                                'clamp(0.8rem, 0.7rem + 0.25vw, 1.1rem)',
                            }}
                            className="text-secondary mb-0"
                          >
                            {review?.reviews}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <NoProductFound />
                  )}
                </div>
              </Tab>
            </Tabs>
          </div>

          {/* Related Products */}
          {relatedProducts && relatedProducts.length > 0 && (
            <div className="related-products">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mb-3">
                <h3 className="mb-2 mb-sm-0">Related Products</h3>
                <p
                  style={{ fontSize: 'clamp(0.7rem, 0.7rem + 0.25vw, 1rem)' }}
                  className="text-muted mb-0"
                >
                  Explore more unique and creative items tailored for you.
                </p>
              </div>
              <div className="row">
                {error && <em className="form_error">{error}</em>}
                {isLoading && <CardSkeleton cards={8} />}
                {relatedProducts.map((product) => (
                  <div
                    key={product.product_id}
                    className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12 mb-4"
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
export default SingleProductPage;
